<script lang="ts">
  import type { CarouselRootProps } from '../carousel.types';

  import { onMount, setContext } from 'svelte';
  import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel';
  import { CaretLeft } from 'phosphor-svelte';

  import { css } from '@agapi/ui/panda/css';
  import { Button } from '../../button';

  import {
    carouselButtonStyles,
    carouselFadeStyles,
    carouselStyles,
    dotStyles,
    indicatorStyles
  } from '../carousel.styles';

  const context = setContext('carousel', { count: 0 });

  const {
    children,
    hideNavigation,
    fadeEdges,
    hideIndicators,
    centerOnOverflow = false,
    options = { align: 'start' },
    rootStyles,
    wrapperStyles,
    css: cssProp
  }: CarouselRootProps = $props();

  let rootNode = $state<HTMLElement>();
  let api: EmblaCarouselType | undefined = $state();

  let canScrollPrev = $state(false);
  let canScrollNext = $state(true);
  let center = $state(false);
  let pages = $state(Math.ceil(context.count / (typeof options.slidesToScroll === 'number' ? options.slidesToScroll : 1))); // Defaults, will be overwritten
  let activeIndex = $state(0);

  onMount(() => {
    if (rootNode) {
      api = EmblaCarousel(rootNode, options);

      pages = api.scrollSnapList().length;

      api.on('select', (api) => {
        canScrollPrev = api.canScrollPrev();
        canScrollNext = api.canScrollNext();
        activeIndex = api.selectedScrollSnap();
        pages = api.scrollSnapList().length;
      });

      api.on('resize', (api) => {
        pages = api.scrollSnapList().length;
      });
    }
  });

  let leftMask = $derived(fadeEdges && canScrollPrev ? 33 : 0);
  let rightMask = $derived(fadeEdges && canScrollNext ? 33 : 0);

  const scrollPrev = () => {
    if (api) {
      api.scrollPrev();
      canScrollPrev = api.canScrollPrev();
      canScrollNext = api.canScrollNext();
    }
  };

  const scrollNext = () => {
    if (api) {
      api.scrollNext();
      canScrollPrev = api.canScrollPrev();
      canScrollNext = api.canScrollNext();
    }
  };

  const checkCanScroll = (api: EmblaCarouselType) => {
    canScrollPrev = api.canScrollPrev();
    canScrollNext = api.canScrollNext();
  };

  const checkCenter = (api: EmblaCarouselType) => {
    center = !api.canScrollNext() && !api.canScrollPrev() && centerOnOverflow;
  };

  $effect(() => {
    if (api) {
      checkCenter(api);
      checkCanScroll(api);

      api.on('resize', (api) => {
        checkCenter(api);
        checkCanScroll(api);
      });
    }
  });

  const classes = $derived(carouselStyles.raw({ center }));
</script>

<div class={css(classes.root, rootStyles)}>
  <div
    bind:this={rootNode}
    style="--leftMask: {leftMask}px; --rightMask: {rightMask}px"
    class={css(classes.wrapper, carouselFadeStyles, wrapperStyles)}
  >
    <div class={css(classes.container, cssProp)}>
      {#if children}
        {@render children()}
      {/if}
    </div>
  </div>

  {#if !hideIndicators}
    <div class={indicatorStyles}>
      {#each Array.from({ length: pages }) as _index, idx}
        <div class={dotStyles({ active: idx === activeIndex })}></div>
      {/each}
    </div>
  {/if}

  {#if !hideNavigation}
    <Button
      size="xs"
      variant="secondary"
      onclick={scrollPrev}
      css={carouselButtonStyles.raw({ direction: 'prev', visible: canScrollPrev })}
      aria-label="Previous"
    >
      <CaretLeft width={21} />
    </Button>

    <Button
      size="xs"
      variant="secondary"
      onclick={scrollNext}
      css={carouselButtonStyles.raw({ direction: 'next', visible: canScrollNext })}
      aria-label="Next"
    >
      <CaretLeft width={21} />
    </Button>
  {/if}
</div>

<style>
  /* Define mask properties to allow for animating gradients */
  @property --leftMask {
    syntax: '<length>';
    inherits: true;
    initial-value: 33px;
  }
  @property --rightMask {
    syntax: '<length>';
    inherits: true;
    initial-value: 33px;
  }
</style>
